import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart3, Search } from "lucide-react";
import { AnalyticsView } from "@/components/AnalyticsView";

function StudentDetail({ uid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (uid) api.get(`/admin/students/${uid}/analytics`).then((r) => setData(r.data));
  }, [uid]);
  return (
    <Dialog open={!!uid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto" data-testid="student-analytics-dialog">
        <DialogHeader>
          <DialogTitle>{data ? `${data.user.name} · ${data.user.email}` : "Loading…"}</DialogTitle>
        </DialogHeader>
        {data ? <AnalyticsView data={data} /> : <Skeleton className="h-64" />}
      </DialogContent>
    </Dialog>
  );
}

export default function StudentActivityTab() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/admin/students/activity").then((r) => setRows(r.data)); }, []);

  const filtered = useMemo(() => (rows || []).filter((r) =>
    `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);

  if (!rows) return <Skeleton className="h-64" />;
  return (
    <Card className="border-border/70" data-testid="student-activity-tab">
      <CardContent className="p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-bold">Student activity</h3>
            <p className="text-xs text-muted-foreground">{rows.length} students · sorted by last activity</p>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-8" data-testid="student-activity-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student</TableHead>
                <TableHead className="text-right">Tests</TableHead>
                <TableHead className="text-right">Completion</TableHead>
                <TableHead className="text-right">Questions</TableHead>
                <TableHead className="text-right">Accuracy</TableHead>
                <TableHead className="text-right">Time</TableHead>
                <TableHead className="text-right">Best</TableHead>
                <TableHead>Last active</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`student-activity-row-${r.id}`}>
                  <TableCell>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.sessions_completed}/{r.sessions_started}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.completion_rate}%</TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.questions_answered}</TableCell>
                  <TableCell className={`text-right font-mono text-xs ${r.accuracy < 50 && r.questions_answered ? "text-rose-600" : ""}`}>{r.accuracy}%</TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.total_time_minutes}m</TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.best_score}%</TableCell>
                  <TableCell className="text-xs">{r.last_active ? new Date(r.last_active).toLocaleString() : "—"}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => setSel(r.id)} data-testid={`student-activity-view-${r.id}`}>
                      <BarChart3 className="mr-1 h-3.5 w-3.5" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow><TableCell colSpan={9} className="py-8 text-center text-xs text-muted-foreground">No students found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <StudentDetail uid={sel} onClose={() => setSel(null)} />
      </CardContent>
    </Card>
  );
}
